import { BADGES_CONFIG } from '@config/badges.config'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { accentColorConfig } from '@hooks/useAccentColor'
import { Agent } from '@typings/dify'
import { AccentColor, Theme } from '@typings/index'
import { Bot, ChevronLeft, ChevronRight, InfoIcon, MessageSquare, ThumbsUp } from 'lucide-react'
import { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import logoDark from '../assets/logo_dark.png'
import logoLight from '../assets/logo_light.png'
import DraggableAgent from './DraggableAgent'
import UserBadge from './UserBadge'

interface SidebarProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  agents: Agent[]
  selectedAgent: Agent | null
  onSelectAgent: (agent: Agent) => void
  onToggleFavorite: (agentId: string) => void
  onReorderAgents: (agents: Agent[]) => void
  accentColor: AccentColor
  theme: Theme
}

const Sidebar = ({
  isOpen,
  setIsOpen,
  agents,
  selectedAgent,
  onSelectAgent,
  onToggleFavorite,
  onReorderAgents,
  accentColor,
  theme,
}: SidebarProps) => {
  const { t } = useTranslation()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragEnd = (result: any) => {
    if (!result.destination) return

    const items = Array.from(agents)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    const reorderedAgents = items.map((agent, index) => ({
      ...agent,
      order: index,
    }))

    onReorderAgents(reorderedAgents)
  }

  const handleDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && !e.target.closest('button') && !e.target.closest('input')) {
      setIsOpen(!isOpen)
    }
  }

  const favoriteAgents = agents.filter((agent) => agent.isFavorite)
  const nonFavoriteAgents = agents.filter((agent) => !agent.isFavorite)
  const totalMessages = agents.reduce((sum, agent) => sum + (agent.stats?.messageCount || 0), 0)
  const totalLikes = agents.reduce((sum, agent) => sum + (agent.stats?.likeCount || 0), 0)
  const currentBadge = BADGES_CONFIG.levels
    .slice()
    .reverse()
    .find((level) => totalLikes >= level.minLikes)

  return (
    <div
      className={`fixed top-0 left-0 h-full bg-white dark:bg-gray-900 transition-all duration-300 ${
        isOpen ? 'w-72' : 'w-16'
      } shadow-lg z-20 flex flex-col`}
      onDoubleClick={handleDoubleClick}
    >
      {/* Header */}
      <div className={`p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800`}>
        <div className={`flex items-center space-x-3 ${isOpen ? '' : 'justify-center'}`}>
          <Bot size={20} className={`${accentColorConfig[accentColor].text} dark:text-white`} />
          {isOpen && (
            <span className="text-xl font-normal text-gray-900 dark:text-white flex items-center">
              <img srcSet={theme === 'dark' ? logoDark : logoLight} alt="GPT Invivo" className="h-7" /> GPT
              <small
                className={`text-xs font-semibold ${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'} px-1 rounded-sm ml-1 mt-1`}
              >
                V2
              </small>
            </span>
          )}
        </div>
      </div>

      {/* Agents List */}
      <div className="flex-1 overflow-y-auto">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="agents">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="py-4">
                {favoriteAgents.length > 0 && (
                  <>
                    {isOpen && (
                      <div className="px-4 mb-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {t('sidebar.favorites')}
                        </span>
                      </div>
                    )}
                    {favoriteAgents.map((agent, index) => (
                      <Draggable key={agent.id} draggableId={agent.id.toString()} index={index}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <DraggableAgent
                              agent={agent}
                              onSelect={onSelectAgent}
                              onToggleFavorite={onToggleFavorite}
                              isSelected={selectedAgent?.id === agent.id}
                              isCollapsed={!isOpen}
                              accentColor={accentColor}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {isOpen && favoriteAgents.length > 0 && nonFavoriteAgents.length > 0 && (
                      <div className="mx-4 my-3 border-t border-gray-200 dark:border-gray-700" />
                    )}
                  </>
                )}

                {nonFavoriteAgents.length > 0 && (
                  <>
                    {isOpen && (
                      <div className="px-4 mb-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {t('sidebar.other_agents')}
                        </span>
                      </div>
                    )}
                    {nonFavoriteAgents.map((agent, index) => (
                      <Draggable key={agent.id} draggableId={agent.id.toString()} index={favoriteAgents.length + index}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <DraggableAgent
                              agent={agent}
                              onSelect={onSelectAgent}
                              onToggleFavorite={onToggleFavorite}
                              isSelected={selectedAgent?.id === agent.id}
                              isCollapsed={!isOpen}
                              accentColor={accentColor}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Mini Dashboard */}
      <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className={`grid ${isOpen ? 'grid-cols-3' : 'grid-cols-1 gap-y-4'} place-items-center`}>
          <div className="flex flex-col items-center justify-center">
            <MessageSquare size={20} className="text-blue-500 dark:text-blue-400" />
            {isOpen && (
              <span className="text-xs mt-2 text-gray-600 dark:text-gray-300 text-center">{totalMessages}</span>
            )}
          </div>
          <div className="flex flex-col items-center justify-center">
            <ThumbsUp size={20} className="text-emerald-500 dark:text-emerald-400" />
            {isOpen && <span className="text-xs mt-2 text-gray-600 dark:text-gray-300 text-center">{totalLikes}</span>}
          </div>
          {currentBadge && (
            <div className="flex flex-col items-center justify-center">
              <UserBadge totalLikes={totalLikes} size={20} />
              {isOpen && (
                <span className="text-xs mt-2 text-gray-600 dark:text-gray-300 text-center">
                  {t(currentBadge.title)}
                </span>
              )}
            </div>
          )}
        </div>
        {isOpen ? (
          <div className="flex items-center space-x-2 mt-5">
            <span className="text-xs font-medium text-gray-500 text-center dark:text-gray-400">
              {t('agents.DISCLAIMER')}
            </span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 mt-5 justify-center" title={t('agents.DISCLAIMER')}>
            <InfoIcon size={20} className="text-gray-500 dark:text-gray-400 text-xs" />
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`absolute top-4 -right-4 z-30 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:${accentColorConfig[accentColor].light} dark:hover:bg-gray-700 transition-colors`}
      >
        {isOpen ? (
          <ChevronLeft size={20} className="dark:text-white" />
        ) : (
          <ChevronRight size={20} className="dark:text-white" />
        )}
      </button>
    </div>
  )
}

export default Sidebar
